import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import { getCurrentUser } from "../services/pocketbase";

const BSODEasterEgg = () => {
  const [active, setActive] = useState(false);
  const [percentage, setPercentage] = useState(1);
  const [completed, setCompleted] = useState(false);
  const [roleAssigned, setRoleAssigned] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenRef = useRef(null);
  const user = getCurrentUser();
  const progressTimerRef = useRef(null);

  // Handle key combination (Ctrl+Alt+B)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.altKey && e.key.toLowerCase() === "b") {
        setActive(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Monitor fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      setIsFullscreen(!!isCurrentlyFullscreen);

      // If fullscreen was exited manually and the experience wasn't completed
      if (!isCurrentlyFullscreen && active && !completed) {
        // Clear any running timers
        if (progressTimerRef.current) {
          clearInterval(progressTimerRef.current);
          progressTimerRef.current = null;
        }

        // Reset the experience
        setActive(false);
        setPercentage(1);
        setCompleted(false);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, [active, completed]);

  // Request fullscreen when activated
  useEffect(() => {
    if (active && !isFullscreen) {
      requestFullscreen();
    }
  }, [active, isFullscreen]);

  // Control cursor visibility and text selection based on active and completed states
  useEffect(() => {
    if (active && isFullscreen && !completed) {
      // Hide cursor when active and not completed
      document.body.style.cursor = "none";
      
      // Disable text selection - enhanced approach
      document.body.style.userSelect = "none";
      document.body.style.webkitUserSelect = "none";
      document.body.style.msUserSelect = "none";
      document.body.style.mozUserSelect = "none";
      
      // Add additional CSS to prevent selection
      const style = document.createElement('style');
      style.id = 'bsod-style';
      style.innerHTML = `
        * {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          pointer-events: none !important;
        }
        .bsod-container {
          pointer-events: auto !important;
        }
      `;
      document.head.appendChild(style);
      
      // Disable browser default behaviors
      const preventDefaultHandler = (e) => {
        e.preventDefault();
        return false;
      };
      
      document.addEventListener('selectstart', preventDefaultHandler);
      document.addEventListener('contextmenu', preventDefaultHandler);
      document.addEventListener('dragstart', preventDefaultHandler);
      
    } else {
      // Show cursor when completed or not active
      document.body.style.cursor = "auto";
      
      // Enable text selection
      document.body.style.userSelect = "auto";
      document.body.style.webkitUserSelect = "auto";
      document.body.style.msUserSelect = "auto";
      document.body.style.mozUserSelect = "auto";
      
      // Remove the added style
      const style = document.getElementById('bsod-style');
      if (style) {
        style.remove();
      }
      
      // Remove event listeners
      document.removeEventListener('selectstart', (e) => e.preventDefault());
      document.removeEventListener('contextmenu', (e) => e.preventDefault());
      document.removeEventListener('dragstart', (e) => e.preventDefault());
    }

    // Cleanup function to ensure cursor and text selection are restored
    return () => {
      document.body.style.cursor = "auto";
      document.body.style.userSelect = "auto";
      document.body.style.webkitUserSelect = "auto";
      document.body.style.msUserSelect = "auto";
      document.body.style.mozUserSelect = "auto";
      
      // Remove the added style
      const style = document.getElementById('bsod-style');
      if (style) {
        style.remove();
      }
      
      // Remove event listeners
      document.removeEventListener('selectstart', (e) => e.preventDefault());
      document.removeEventListener('contextmenu', (e) => e.preventDefault());
      document.removeEventListener('dragstart', (e) => e.preventDefault());
    };
  }, [active, completed, isFullscreen]);

  const requestFullscreen = () => {
    if (fullscreenRef.current) {
      try {
        if (fullscreenRef.current.requestFullscreen) {
          fullscreenRef.current.requestFullscreen();
        } else if (fullscreenRef.current.webkitRequestFullscreen) {
          fullscreenRef.current.webkitRequestFullscreen();
        } else if (fullscreenRef.current.msRequestFullscreen) {
          fullscreenRef.current.msRequestFullscreen();
        } else if (fullscreenRef.current.mozRequestFullScreen) {
          fullscreenRef.current.mozRequestFullScreen();
        }
      } catch (err) {
        console.error("Fullscreen request failed:", err);
        // If fullscreen fails, don't continue with the experience
        setActive(false);
      }
    }
  };

  // Exit fullscreen
  const exitFullscreen = () => {
    try {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      }
    } catch (err) {
      console.error("Exit fullscreen failed:", err);
    }
  };

  // Progress percentage over time
  useEffect(() => {
    if (!active || completed || !isFullscreen) return;

    const totalDuration = 60000; // 1 minute in milliseconds (changed from 120000)
    const interval = 12000; // Update every 12 seconds (adjusted for shorter duration, was 24000)
    const steps = totalDuration / interval;
    const increment = 99 / steps; // From 1% to 100%

    // Clear any existing timer
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
    }

    progressTimerRef.current = setInterval(() => {
      setPercentage((prev) => {
        const newValue = prev + increment;
        if (newValue >= 100) {
          clearInterval(progressTimerRef.current);
          progressTimerRef.current = null;
          setCompleted(true);
          return 100;
        }
        return newValue;
      });
    }, interval);

    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    };
  }, [active, completed, isFullscreen]);

  // Assign Discord role when completed
  useEffect(() => {
    // Only assign role if the experience was completed properly in fullscreen
    if (completed && isFullscreen && !roleAssigned && user?.discord_id) {
      const assignRole = async () => {
        try {
          const response = await fetch("/api/discord/assign-role", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              discordId: user.discord_id,
              roleId: "1372487977573285992", // Using the server ID as a placeholder, replace with actual role ID
            }),
          });

          const data = await response.json();
          if (data.success) {
            setRoleAssigned(true);
          } else {
            setError(data.error || "Failed to assign role");
          }
        } catch (err) {
          setError("Network error when assigning role");
        }
      };

      assignRole();
    }
  }, [completed, roleAssigned, user, isFullscreen]);

  // Handle refresh button click
  // eslint-disable-next-line
  const handleRefresh = () => {
    exitFullscreen();
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  // Handle return to website button click
  const handleReturn = () => {
    exitFullscreen();
    setActive(false);
    setPercentage(1);
    setCompleted(false);
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
    };
  }, []);

  if (!active) return null;

  return (
    <Box
      ref={fullscreenRef}
      sx={{
        display: active ? "flex" : "none",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "#0078d7",
        color: "white",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        padding: "150px 20px 20px 20px",
        zIndex: 9999,
        overflow: "hidden",
      }}
      className="bsod-container"
    >
      {!completed ? (
        // BSOD Screen
        <>
          <Box sx={{ fontSize: "100px", mb: 4 }}>:(</Box>
          <Typography variant="h4" sx={{ mb: 4 }}>
            Your PC ran into a problem and needs to restart. We're just
            collecting some error info, and then we'll restart for you
          </Typography>
          <Typography variant="h5" sx={{ mb: 4 }}>
            {Math.floor(percentage)}% complete
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              component="img"
              src="/frame.png"
              alt="QR Code"
              sx={{ width: 96, height: 96, mr: 2 }}
            />
            <Box>
              <Typography>
                For more information about this issue and possible fixes, visit
                http://windows.com/stopcode
              </Typography>
              <Typography sx={{ mt: 2 }}>
                If you call a support person, give them this info:
              </Typography>
              <Typography sx={{ mt: 1 }}>
                Stop code: CRITICAL_PROCESS_DIED
              </Typography>
            </Box>
          </Box>
        </>
      ) : (
        // Congratulations Screen
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            textAlign: "center",
          }}
        >
          <Typography variant="h2" sx={{ mb: 4 }}>
            Congratulations!
          </Typography>
          <Typography variant="h5" sx={{ mb: 4 }}>
            You've discovered our BSOD Easter Egg!
          </Typography>

          {user?.discord_id ? (
            roleAssigned ? (
              <Typography variant="h6" sx={{ mb: 4 }}>
                A special role has been assigned to your Discord account.
              </Typography>
            ) : error ? (
              <Typography variant="h6" color="error" sx={{ mb: 4 }}>
                {error}
              </Typography>
            ) : (
              <Typography variant="h6" sx={{ mb: 4 }}>
                Assigning Discord role...
              </Typography>
            )
          ) : (
            <Typography variant="h6" color="error" sx={{ mb: 4 }}>
              Please link your Discord account to receive your special role.
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleReturn}
            sx={{ mt: 4 }}
          >
            Return to Website
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BSODEasterEgg;
